import { Collapsible, useCollapsible } from '@/components/shared/Collapsible';
import { Section, SectionParagraph, SectionTitle } from '@/components/shared/Content';
import chevron from '@/static/assets/chevron.png';
import { Flex, font, preventOneWordInLastLine, themedColor } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import { COLORS } from '@lemonade-hq/tokens';
import parse from 'html-react-parser';
import Image from 'next/image';
import type React from 'react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

const StyledSectionTitle = styled(SectionTitle)`
  font-size: 2.8rem;
  ${MEDIA_QUERIES.tabletAndDesktop`
    font-size: 4rem;
  `}
`;

const StyledSectionParagraph = styled(SectionParagraph)`
  font-size: 1.6rem;
  ${MEDIA_QUERIES.tabletAndDesktop`
    font-size: 1.8rem;
  `}
`;

const FAQBox = styled.div`
  padding: 20px;
  border-radius: 10px;
  width: 100%;
  background-color: ${COLORS.white};
  height: auto;
  transition: all 0.2s ease;
  cursor: pointer;

  ${MEDIA_QUERIES.desktop`
    padding: 30px;
    cursor: text;
  `}
`;

const FAQContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;
  align-items: stretch;
  text-align: left;
  max-width: 1200px;
  width: 100%;

  ${MEDIA_QUERIES.desktop`
    gap: 30px;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-rows: minmax(125px, auto);

    &:has(${FAQBox}:hover) ${FAQBox}:not(:hover) {
      opacity: 0.5;
    }

    ${FAQBox}:hover {
      transform: scale(1.08);
    }
  `}
`;

const FAQTitle = styled.h3`
  ${font('main', {
    fontSize: '1.8rem',
    lineHeight: '2.4rem',
    fontWeight: 'bold',
  })}
  color: ${themedColor('night')};
  white-space: normal;

  ${MEDIA_QUERIES.desktop`
    ${font('main', {
      fontSize: '2.4rem',
      lineHeight: '3rem',
      fontWeight: 'bold',
    })}
    white-space: pre-line;
  `}
`;

const FAQContent = styled.p`
  ${font('main', { fontSize: '1.2rem', lineHeight: 1.5 })}
  color: ${themedColor('boulder')};
  margin-top: 20px;
  white-space: normal;

  ${MEDIA_QUERIES.tabletAndDesktop`
    ${font('main', { fontSize: '1.4rem', lineHeight: 1.6 })}
    white-space: pre-line;
  `}
`;

const StyledChevron = styled(Image)`
  display: block;
  transition: transform 0.2s ease;
  pointer-events: none;
  ${MEDIA_QUERIES.desktop`
    display: none;
  `}

  &.open {
    transform: rotate(180deg);
  }

  &.closed {
    transform: rotate(0deg);
  }
`;

interface FAQItemProps {
  title: string;
  content: string;
}

const FAQItem: React.FC<FAQItemProps> = ({ title, content }) => {
  const { open, toggle } = useCollapsible();
  const [isDesktopViewport, setIsDesktopViewport] = useState(true);

  useEffect(() => {
    setIsDesktopViewport(window.innerWidth > 990);
    const handleResize = () => {
      setIsDesktopViewport(window.innerWidth > 990);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <FAQBox
      onClick={() => {
        if (!isDesktopViewport) toggle();
      }}
      tabIndex={0}
    >
      <Flex justifyContent="space-between" gap="15px" alignItems="center">
        <FAQTitle>{preventOneWordInLastLine(title)}</FAQTitle>
        <StyledChevron src={chevron} height={8} width={15} alt="" className={open ? 'open' : 'closed'} />
      </Flex>

      <Collapsible open={isDesktopViewport ? true : open}>
        <FAQContent>{parse(content)}</FAQContent>
      </Collapsible>
    </FAQBox>
  );
};

interface FAQData {
  question: string;
  answer: string;
}

export interface GoodToKnowSectionProps {
  title: string;
  subtitle: string;
  faqs: FAQData[];
}

export const GoodToKnowSection: React.FC<GoodToKnowSectionProps> = ({ title, subtitle, faqs }) => {
  return (
    <Section darkBackground py={[60, 100]} px={20}>
      <Flex flexDirection="column" gap="20px" alignItems="center" mb={[30, 50]}>
        <StyledSectionTitle>{title}</StyledSectionTitle>
        <StyledSectionParagraph>{subtitle}</StyledSectionParagraph>
      </Flex>

      <FAQContainer>
        {faqs.map(({ question, answer }) => (
          <FAQItem key={question} title={question} content={answer} />
        ))}
      </FAQContainer>
    </Section>
  );
};
